import { Dialog, DialogContent, Grid, Slide, Typography } from '@mui/material';
import { find, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { forwardRef, useContext, useEffect, useState } from 'react';

import {
  addPrescriptionNotes,
  getAllOrders,
  listPrescriptionNotes,
} from 'store/thunks/patientThunks';
import { loadPrescriptionData } from 'store/thunks/workQueueThunks';
import { getPrescriptionOverrideAuditsApi } from 'api/prescription';
import { ContextType } from 'common/constants/notes';
import LoadingModule from 'common/components/LoadingModule';

import { PrescriptionReviewPayloadContext } from 'common/hooks/usePrescriptionReviewPayloadContext';
import PatientPrescriptionView from './PatientPrescriptionView';

const Transition = forwardRef((props, ref) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Slide direction='up' ref={ref} {...props} timeout={300} />
));

const PatientPrescriptionDialog = ({
  open,
  mpi,
  contextId,
  closeDialog,
  isPrescriptionReview,
  reviewType,
  isPrescriptionsTab,
  isManualReview,
}) => {
  const dispatch = useDispatch();
  const orders = useSelector(({ patient }) => patient[mpi]?.allOrders);
  const prescriptionList = useSelector(({ patient }) => patient[mpi]?.prescriptions);
  const prescriptionFromWorkQueue = useSelector(({ workQueue }) => workQueue?.prescriptionReview);
  const currentPage = prescriptionFromWorkQueue?.currentPage;
  const [viewPharmacyTranslation] = useState(true);
  const [prescriptionLoading, setPrescriptionLoading] = useState(true);
  const [overrideDetails, setOverrideDetails] = useState({});
  const prescriptionObject = find(prescriptionFromWorkQueue?.pages[currentPage], {
    pomPrescriptionId: contextId,
  });
  const patientData = prescriptionObject?.patient;
  const prescriptionData = prescriptionObject?.prescriptionData;
  const directionReview = prescriptionObject?.directionReview;
  const prescriptionDataLoaded = prescriptionObject?.prescriptionDataLoaded;
  const prescription = !isEmpty(prescriptionList)
    ? find(prescriptionList, { prescriptionUUID: contextId })
    : prescriptionObject;
  const lineItemId = prescriptionObject?.lineItem?.requestLineItemId || prescription?.activeItemId;
  const { payload } = useContext(PrescriptionReviewPayloadContext);

  const prescriptionNotes = prescription?.notes;

  useEffect(() => {
    dispatch(
      listPrescriptionNotes({
        mpi,
        contextId,
        contextType: ContextType.PRESCRIPTION,
        isPrescriptionReview,
      })
    );
  }, [mpi, dispatch, contextId, isPrescriptionReview]);

  useEffect(() => {
    if (isPrescriptionReview && lineItemId && contextId && reviewType && !prescriptionDataLoaded) {
      setPrescriptionLoading(true);
      dispatch(
        loadPrescriptionData({
          lineItemId,
          prescriptionId: contextId,
          reviewType,
        })
      ).finally(() => setPrescriptionLoading(false));
    }
  }, [dispatch, isPrescriptionReview, lineItemId, contextId, reviewType, prescriptionDataLoaded]);

  const onAddNote = (message, onComplete) => {
    const content = {
      message,
      contentType: 'TEXT',
    };
    const commentContext = {
      id: contextId,
      type: ContextType.PRESCRIPTION,
    };

    return dispatch(
      addPrescriptionNotes({
        mpi,
        commentContext,
        content,
        contextId,
        isPrescriptionReview,
      })
    ).then(() => {
      onComplete(true);
    });
  };

  const handlePrint = () => window.print();

  const getOverrideDetails = () =>
    getPrescriptionOverrideAuditsApi(contextId)
      .then((overrideData) => setOverrideDetails(overrideData))
      .finally(() => setPrescriptionLoading(false));

  useEffect(() => {
    getOverrideDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextId]);

  useEffect(() => {
    dispatch(
      getAllOrders({
        mpi,
        includeAllFacilities: true,
      })
    );
  }, [dispatch, mpi]);

  return (
    <>
      <style>
        {`
        @media print {
          body {
            visibility: hidden;
          }

          .PrescriptionDialog {
            visibility: visible !important;
          }

          .PrescriptionBtnOptions {
           display: none;
          }

          .FooterMessage {
            display: block !important;
            position: fixed;
            bottom: 0;
            width: 100%;
            text-align: center;
            padding: 0.5cm;
            background-color: #f0f0f0;
          }
        }
        `}
      </style>

      <Dialog
        fullScreen
        open={open}
        TransitionComponent={Transition}
        className='PrescriptionDialog'
      >
        {prescriptionLoading ? (
          <LoadingModule />
        ) : (
          <DialogContent sx={{ px: 0, pt: 0, bgcolor: 'grey.100' }}>
            <PatientPrescriptionView
              mpi={mpi}
              reviewType={reviewType}
              patientData={patientData}
              prescriptionId={contextId}
              overrideDetails={overrideDetails}
              showTranslation={viewPharmacyTranslation}
              prescriptionData={prescriptionData}
              directionReview={directionReview}
              isPrescriptionReview={isPrescriptionReview}
              lineItemId={lineItemId}
              orders={orders}
              isPrescriptionsTab={isPrescriptionsTab}
              onPrint={handlePrint}
              onClose={() => closeDialog(payload)}
              notes={prescriptionNotes}
              onAddNote={onAddNote}
              isManualReview={isManualReview}
              onRefreshOverrideDetails={getOverrideDetails}
            />

            <Grid className='FooterMessage' sx={{ display: 'none' }}>
              <Typography>This is printed copy and not the original</Typography>
            </Grid>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

export default PatientPrescriptionDialog;

PatientPrescriptionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mpi: PropTypes.string.isRequired,
  closeDialog: PropTypes.func.isRequired,
  contextId: PropTypes.string.isRequired,
  isPrescriptionReview: PropTypes.bool,
  reviewType: PropTypes.string,
  isPrescriptionsTab: PropTypes.bool,
  isManualReview: PropTypes.bool,
};

PatientPrescriptionDialog.defaultProps = {
  isPrescriptionReview: false,
  reviewType: '',
  isPrescriptionsTab: false,
  isManualReview: false,
};
